import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { deleteGalleryImageById, fetchGalleryData, uploadGalleryData } from '../../services/gallery.service';
import config from '../../environment/config';
import { SnackbarUI, useSnackbar } from '../header/snackbarProvider';
import { FaEye, FaMinus, FaPlus, FaTrash } from 'react-icons/fa';
import ConfirmationDialog from '../header/confirmation.dialog';
import * as bootstrap from "bootstrap";
import './gallery.component.css';

const GalleryList = () => {
  const API_URL = config.API_URL;
  const [currentAction, setCurrentAction] = useState("list");
  const [gallery, setGallery] = useState([]);
  const { showSnackbar, snackbar } = useSnackbar();
  const fileInputRefs = useRef([]);
  const [deleteGalleryConfirmationDialog, setDeleteGalleryConfirmationDialog] = useState(false);
  const [openGalleryConfirmationDialog, setOpenGalleryConfirmationDialog] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [galleryId, setGalleryId] = useState(null);
  const [activeTab, setActiveTab] = useState("ALL");


  const handleAddClick = () => {
    setCurrentAction("add");
  };

  const handleCloseForm = () => {
    setCurrentAction("list");
  };

  const fetchGallleryList = async () => {
    try {
      const list = await fetchGalleryData();
      setGallery(list || []);
    } catch (error) {
      console.error("Error fetching event notices:", error);
      showSnackbar("Error fetching gallery list", "danger");
    }
  };

  useEffect(() => {
    fetchGallleryList();
  }, []);

  const handleDeleteImage = async (id) => {
    setDeleteGalleryConfirmationDialog(true);
    setGalleryId(id);
  };

  const groupedByYear = gallery.reduce((acc, item) => {
    const year = item.year;
    if (!acc[year]) acc[year] = [];
    acc[year].push(item);
    return acc;
  }, {});

  const sortedYears = Object.keys(groupedByYear).sort((a, b) => Number(b) - Number(a));

  const handlePreview = (imageUrl, description) => {
    setPreviewImage({ imageUrl, description });
    const modal = new bootstrap.Modal(document.getElementById("imagePreviewModal"));
    modal.show();
  };

  const getImagesToShow = () => {
    if (activeTab === "ALL") {
      return sortedYears.flatMap(year => groupedByYear[year]);
    } else {
      return groupedByYear[activeTab] || [];
    }
  };

  const [entries, setEntries] = useState([
    { description: "", year: "", images: [null] }
  ]);

  const handleChange = (index, field, value) => {
    const newEntries = [...entries];
    newEntries[index][field] = value;
    setEntries(newEntries);
  };

  const addRow = () => {
    setEntries([...entries, { description: "", year: "", images: [null] }]);
  };

  const removeRow = (index) => {
    const newEntries = [...entries];
    newEntries.splice(index, 1);
    setEntries(newEntries);
  };

  const addImageInput = (entryIndex) => {
    const newEntries = [...entries];
    newEntries[entryIndex].images.push(null);
    setEntries(newEntries);
  };

  const removeImageInput = (entryIndex, imageIndex) => {
    const newEntries = [...entries];
    newEntries[entryIndex].images.splice(imageIndex, 1);
    setEntries(newEntries);
  };

  const handleFileChange = (entryIndex, imageIndex, file, event) => {
    if (!file || !file.type.startsWith("image/")) {
      showSnackbar("Only image files are allowed.", "danger");
      event.target.value = "";
      return;
    }

    const newEntries = [...entries];
    newEntries[entryIndex].images[imageIndex] = file;
    setEntries(newEntries);
  };

  const handleGalleryDialogClose = () => {
    setOpenGalleryConfirmationDialog(false);
  };

  const handleDeleteGalleryDialogClose = () => {
    setDeleteGalleryConfirmationDialog(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const validImages = entry.images.filter(img => img !== null);

      if (!entry.description || !entry.year || validImages.length === 0) {
        showSnackbar("All fields are required for each row, including at least one image.", "danger");
        return;
      }
    }

    setOpenGalleryConfirmationDialog(true);
  };

  const handleGalleryDialogConfirm = async () => {
    setOpenGalleryConfirmationDialog(false);
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const validImages = entry.images.filter(img => img !== null);

      if (!entry.description || !entry.year || validImages.length === 0) {
        showSnackbar("All fields are required for each row, including at least one image.", "danger");
        return;
      }

      const formData = new FormData();
      validImages.forEach(file => formData.append("images", file));
      formData.append("description", entry.description);
      formData.append("year", entry.year);

      try {
        const result = await uploadGalleryData(formData);
        if (result && typeof result === "string") {
          showSnackbar(result, "success");
          setCurrentAction("list");
          fetchGallleryList();
        } else {
          showSnackbar("Upload response invalid.", "danger");
        }
      } catch (err) {
        console.error("Upload failed:", err);
        showSnackbar(
          err?.response?.data || "Server error occurred while uploading.",
          "danger"
        );
      }

      // Clear file input elements
      if (fileInputRefs.current[i]) {
        fileInputRefs.current[i].forEach(ref => {
          if (ref) ref.value = null;
        });
      }
    }

    // Reset form
    setEntries([{ description: "", year: "", images: [null] }]);
  };


  const handleDeleteGalleryDialogConfirm = async () => {
    setDeleteGalleryConfirmationDialog(false);
      try {
        const result = await deleteGalleryImageById(galleryId);
        if (result && typeof result === "string") {
          showSnackbar(result, "success");
          setCurrentAction("list");
          fetchGallleryList();
        } else {
          showSnackbar("Delete response invalid.", "danger");
        }
      } catch (error) {
        console.error("Delete failed:", error);
        showSnackbar("An error occurred while deleting image.", "danger");
      }
  };


  return (
    <div className="container p-3">
      {currentAction === 'list' && (
        <>
          <div className="subscription-title mb-4">
            <h1>Gallery List</h1>
            <button className="btn add btn-name" onClick={handleAddClick}>
              Add New Gallery
            </button>
          </div>
          {snackbar.open && <SnackbarUI message={snackbar.message} type={snackbar.type} />}
          <ul className="nav nav-pills mb-4 flex-wrap justify-content-center">
            <li className="nav-item">
              <button
                className={`nav-link gallery-tab ${activeTab === "ALL" ? "active" : ""}`}
                onClick={() => setActiveTab("ALL")}
              >
                ALL
              </button>
            </li>
            {sortedYears.map((year) => (
              <li className="nav-item" key={year}>
                <button
                  className={`nav-link gallery-tab ${activeTab === year ? "active" : ""}`}
                  onClick={() => setActiveTab(year)}
                >
                  {year}
                </button>
              </li>
            ))}
          </ul>
          <section className="gallery">
            {getImagesToShow().map((img, idx) => (
              <div className="image shadow-card p-1" key={idx}>
                <img
                  src={`${API_URL}uploads/${img.imageUrl}`}
                  className="img-fluid"
                  alt={img.description}
                  loading="lazy"
                />
                <div className="gallery-overlay">
                  <button
                    className="btn btn-light me-2 btn-sm"
                    onClick={() => handlePreview(img.imageUrl, img.description)}
                  >
                    <FaEye />
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteImage(img.galleryId)}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </section>




          {/* Modal */}
          <div
            className="modal fade"
            id="imagePreviewModal"
            tabIndex="-1"
            aria-labelledby="imagePreviewModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body text-center">
                  {previewImage && (
                    <img
                      src={`${API_URL}uploads/${previewImage.imageUrl}`}
                      alt={previewImage.description}
                      className="img-fluid rounded shadow"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* <button className="btn add btn-name" onClick={handleAddClick}>
            Add New Gallery
          </button> */}
          <ConfirmationDialog
            open={deleteGalleryConfirmationDialog}
            onClose={handleDeleteGalleryDialogClose}
            onConfirm={handleDeleteGalleryDialogConfirm}
            title="Confirm Delete"
            message="Are you sure you want to delete?"
          />
        </>
      )}

      {/* Upload Form */}
      {currentAction === 'add' && (
        <div className="subscription-title">
          <h1>Upload Gallery Items</h1>
          {snackbar.open && <SnackbarUI message={snackbar.message} type={snackbar.type} />}
          <form onSubmit={handleSubmit} className="container p-3">
            {entries.map((entry, index) => (
              <div className="row mb-4 border rounded p-3 shadow-card" key={index}>
                <div className="col-12 col-sm-6 col-md-5 mb-3">
                  <label className="form-label"><strong>Description</strong></label>
                  <input
                    type="text"
                    value={entry.description}
                    onChange={(e) => handleChange(index, "description", e.target.value)}
                    className="form-control"
                  />
                </div>

                <div className="col-12 col-sm-6 col-md-3 mb-3">
                  <label className="form-label"><strong>Year</strong></label>
                  <select
                    value={entry.year}
                    onChange={(e) => handleChange(index, "year", e.target.value)}
                    className="form-control"
                  >
                    <option value="">Select Year</option>
                    {Array.from({ length: 10 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      const yearUsed = entries.some((e, i2) => i2 !== index && e.year === year.toString());
                      return (
                        <option key={year} value={year} disabled={yearUsed}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* <div className="col-12 col-md-4 d-flex gap-2 align-items-end mb-3">
                  <button type="button" className="btn btn-success" onClick={addRow}><FaPlus /></button>
                  {entries.length > 1 && (
                    <button type="button" className="btn btn-danger" onClick={() => removeRow(index)}><FaMinus /></button>
                  )}
                </div> */}

                <div className="col-12 mt-2">
                  <label className="form-label"><strong>Images</strong></label>
                  {entry.images.map((img, imageIndex) => (
                    <div key={imageIndex} className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2 mb-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(index, imageIndex, e.target.files[0], e)}
                        ref={(el) => {
                          if (!fileInputRefs.current[index]) fileInputRefs.current[index] = [];
                          fileInputRefs.current[index][imageIndex] = el;
                        }}
                        className="form-control"
                        style={{ maxWidth: "400px" }}
                      />
                      <div className="d-flex gap-2 mt-2 mt-md-0">
                        <button type="button" className="btn btn-outline-success" onClick={() => addImageInput(index)}>
                          <FaPlus />
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => removeImageInput(index, imageIndex)}
                          disabled={entry.images.length === 1}
                        >
                          <FaMinus />
                        </button>
                      </div>
                      {img && <small>1 file selected</small>}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="text-center">
              <button type="submit" className="btn btn-primary me-2">Upload All</button>
              <button type="button" className="btn btn-secondary" onClick={handleCloseForm}>Cancel</button>
            </div>
          </form>
          <ConfirmationDialog
            open={openGalleryConfirmationDialog}
            onClose={handleGalleryDialogClose}
            onConfirm={handleGalleryDialogConfirm}
            title="Confirm Submit"
            message="Are you sure you want to submit?"
          />
        </div>
      )}
    </div>
  );
};

export default GalleryList;